import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Cart } from '@/api/cart';
import { playBeep } from '@/utils/sound';
import { useBusinessStore } from './useBusinessStore';

interface POSCartState {
  cart: Cart | null;
  loading: boolean;
  error: string | null;
  loadCart: (businessId: string) => Promise<void>;
  addItem: (businessId: string, product: any, quantity?: number) => Promise<void>;
  updateQuantity: (businessId: string, itemId: string, quantity: number) => Promise<void>;
  removeItem: (businessId: string, itemId: string) => Promise<void>;
  scanItem: (businessId: string, barcode: string) => Promise<boolean>;
  clearCart: () => void;
  setError: (error: string | null) => void;
}

export const usePOSCartStore = create<POSCartState>()(
  persist(
    (set, get) => ({
      cart: null,
      loading: false,
      error: null,

      setError: (error) => set({ error }),

      // We still keep loadCart as a no-op or just local initialization if needed, 
      // but in offline mode the cart is restored automatically by persist middleware.
      loadCart: async (businessId: string) => {
        // Only load if empty to initialize
        if (!get().cart) {
          set({
            cart: {
              id: `local-cart-${businessId}`,
              business_id: businessId,
              total_amount: 0,
              cart_items: [],
            } as any
          });
        }
      },

      addItem: async (businessId: string, product: any, quantity = 1) => {
        // 1. Instant feedback
        playBeep();

        const currentCart = get().cart;
        let newItems = currentCart ? [...currentCart.cart_items] : [];
        let newTotal = currentCart ? parseFloat(currentCart.total_amount.toString()) : 0;

        const existingIndex = newItems.findIndex(i => i.product_id === product.id);
        const nowISO = new Date().toISOString();
        
        if (existingIndex >= 0) {
          newItems[existingIndex] = {
            ...newItems[existingIndex],
            quantity: newItems[existingIndex].quantity + quantity,
          };
          newTotal += quantity * product.price;
        } else {
          newItems.push({
            id: `temp-${Date.now()}`,
            product_id: product.id,
            quantity: quantity,
            created_at: nowISO,
            product: {
              id: product.id,
              name: product.name,
              price: product.price,
              barcode: product.manufacturer_barcode || '',
              image_url: null
            }
          } as any);
          newTotal += quantity * product.price;
        }

        set({ 
          cart: { 
            id: currentCart?.id || `local-cart-${businessId}`,
            business_id: businessId,
            total_amount: newTotal,
            cart_items: newItems
          } as any
        });
      },

      updateQuantity: async (businessId: string, itemId: string, quantity: number) => {
        const currentCart = get().cart;
        if (!currentCart) return;

        const newItems = currentCart.cart_items.map(item => {
          if (item.id === itemId) {
            return { ...item, quantity };
          }
          return item;
        }).filter(item => item.quantity > 0);

        const newTotal = newItems.reduce((sum, item: any) => {
          const productData = item.product || item.products;
          const price = productData?.price || 0;
          return sum + (price * item.quantity);
        }, 0);

        set({ 
          cart: { 
            ...currentCart,
            cart_items: newItems,
            total_amount: newTotal
          } 
        });
      },

      removeItem: async (businessId: string, itemId: string) => {
        const currentCart = get().cart;
        if (!currentCart) return;

        const newItems = currentCart.cart_items.filter(item => item.id !== itemId);
        const newTotal = newItems.reduce((sum, item) => {
          const productData = (item as any).product || (item as any).products;
          const price = productData?.price || 0;
          return sum + (price * item.quantity);
        }, 0);

        set({ 
          cart: { 
            ...currentCart,
            cart_items: newItems,
            total_amount: newTotal
          } 
        });
      },

      scanItem: async (businessId: string, barcode: string) => {
        // Look up offline products
        const products = useBusinessStore.getState().products;
        const matchedProduct = products.find(
          p => p.barcode === barcode || (p as any).manufacturer_barcode === barcode || p.sku === barcode
        );

        if (matchedProduct) {
          await get().addItem(businessId, matchedProduct, 1);
          return true;
        } else {
          set({ error: `Product with barcode ${barcode} not found offline.` });
          throw new Error('Item not found');
        }
      },

      clearCart: () => {
        set({ cart: null, error: null });
      }
    }),
    {
      name: 'pos-cart-storage',
    }
  )
);
